// Store para sanciones de Recursos Humanos
import sancionesRecursosHumanosService from '@/services/sancionesRecursosHumanos.service.js';

// Variable para almacenar el timeout del debounce
let searchDebounceTimer = null;

export default {
  namespaced: true,
  state: {
    buscar: null,
    estadoSeleccionado: null,
    tabActivo: 0, // 0: Seguimiento de reportes, 1: Anexar al expediente
    sanciones: [],
    solicitudActiva: null, // Solicitud actualmente seleccionada
    sancionHistoricaActiva: null, // Sanción histórica seleccionada para visualizar
    cargandoSolicitud: false,
    // Paginación
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    // Todos los estados disponibles desde el catálogo
    todosLosEstados: [],
    // IDs de estados por tab (se filtrarán de todosLosEstados)
    idsSeguimiento: [1, 2, 3, 4, 5, 6, 7, 8, 10],
    idsAnexar: [8],
    listaSanciones: []
  },
  getters: {
    tabActivo: state => state.tabActivo,
    estadosActuales: state => {
      const idsPermitidos = state.tabActivo === 0 ? state.idsSeguimiento : state.idsAnexar;
      // Filtrar los estados según los IDs permitidos para el tab actual
      return state.todosLosEstados.filter(estado => idsPermitidos.includes(estado.id));
    },
    sancionesFiltradas: state => {
      // Ya no filtramos en el frontend, la búsqueda se hace en el backend
      return state.sanciones;
    },
    paginacion: state => ({
      page: state.page,
      per_page: state.per_page,
      total: state.total,
      last_page: state.last_page,
    }),
  },

  mutations: {
    setTabActivo(state, tab) {
      state.tabActivo = tab;
    },
    setBuscar(state, buscar) {
      state.buscar = buscar;
    },
    setEstadoSeleccionado(state, estado) {
      state.estadoSeleccionado = estado;
    },
    limpiarBuscar(state) {
      state.buscar = null;
    },
    limpiarFiltros(state) {
      state.buscar = null;
      state.estadoSeleccionado = null;
    },
    setTodosLosEstados(state, estados) {
      state.todosLosEstados = estados;
    },
    setSanciones(state, sanciones) {
      state.sanciones = sanciones;
    },
    setPaginacion(state, { page, per_page, total, last_page }) {
      state.page = page;
      state.per_page = per_page;
      state.total = total;
      state.last_page = last_page;
    },
    setPage(state, page) {
      state.page = page;
    },
    setSolicitudActiva(state, solicitud) {
      state.solicitudActiva = solicitud;
    },
    setSancionHistoricaActiva(state, sancion) {
      state.sancionHistoricaActiva = sancion;
    },
    setCargandoSolicitud(state, cargando) {
      state.cargandoSolicitud = cargando;
    },
    setlistaSanciones(state, lista) {
      state.listaSanciones = lista;
    }
  },
  actions: {
    async fetchEstados({ commit }) {
      try {
        const estados = await sancionesRecursosHumanosService.fetchEstados();
        commit('setTodosLosEstados', estados);
      } catch (error) {
        const estadosPorDefecto = [];
        commit('setTodosLosEstados', estadosPorDefecto);
      }
    },
    cambiarTab({ commit, dispatch, state }, tab) {
      commit('setTabActivo', tab);
      commit('limpiarFiltros'); // Limpiar filtros al cambiar de tab
      commit('setPage', 1); // Resetear a página 1
      dispatch('fetchSanciones');
    },
    actualizarBuscar({ commit, dispatch }, buscar) {
      commit('setBuscar', buscar);
      commit('setPage', 1); // Resetear a página 1 al buscar
      
      // Limpiar el timeout anterior si existe
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
      
      // Si la búsqueda está vacía, llamar inmediatamente
      if (!buscar || buscar.trim() === '') {
        dispatch('fetchSanciones');
        return;
      }
      
      // Crear nuevo timeout de 700ms
      searchDebounceTimer = setTimeout(() => {
        dispatch('fetchSanciones');
      }, 700);
    },
    actualizarEstado({ commit, dispatch }, estado) {
      commit('setEstadoSeleccionado', estado);
      commit('setPage', 1); // Resetear a página 1
      // No disparar fetchSanciones aquí, se hará con el botón buscar
    },
    limpiarBuscar({ commit, dispatch }) {
      // Limpiar el timeout si existe
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
        searchDebounceTimer = null;
      }
      
      commit('limpiarBuscar');
      commit('setPage', 1); // Resetear a página 1
      dispatch('fetchSanciones'); // Llamar al backend sin el parámetro search
    },
    limpiarFiltros({ commit, dispatch }) {
      // Limpiar el timeout si existe
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
        searchDebounceTimer = null;
      }
      
      commit('limpiarFiltros');
      commit('setPage', 1); // Resetear a página 1
      dispatch('fetchSanciones');
    },
    cambiarPagina({ commit, dispatch, state }, payload) {
      let page = payload;
      let perPage = null;

      if (payload && typeof payload === 'object') {
        page = payload.page ?? state.page;
        perPage = payload.per_page ?? null;
      }

      if (perPage && perPage !== state.per_page) {
        commit('setPaginacion', {
          page: 1,
          per_page: perPage,
          total: state.total,
          last_page: state.last_page,
        });
        dispatch('fetchSanciones');
        return;
      }

      commit('setPage', page || 1);
      dispatch('fetchSanciones');
    },

    async fetchSanciones({ commit, state }) {
      try {
        // Mostrar loader
        this.commit('utils/setLoader', true, { root: true });

        // Preparar parámetros para la petición
        const params = {
          page: state.page,
          per_page: state.per_page,
        };

        // Si estamos en el tab "Anexar al expediente" (tab 1), agregar la bandera
        if (state.tabActivo === 1) {
          params.amonestaciones = true;
        }

        // Si hay búsqueda activa, agregar el parámetro search
        if (state.buscar) {
          params.search = state.buscar;
        }

        // Si hay estado seleccionado, agregar el parámetro estado
        if (state.estadoSeleccionado) {
          params.estado = state.estadoSeleccionado;
        }

        // Llamar al servicio
        const response = await sancionesRecursosHumanosService.fetchSanciones(params);
        
        // Guardar los datos y la paginación en el store
        commit('setSanciones', response.data || []);
        commit('setPaginacion', {
          page: response.page,
          per_page: response.per_page,
          total: response.total,
          last_page: response.last_page,
        });
      } catch (error) {
        commit('setSanciones', []);
        commit('setPaginacion', {
          page: 1,
          per_page: 10,
          total: 0,
          last_page: 1,
        });
      } finally {
        // Ocultar loader
        this.commit('utils/setLoader', false, { root: true });
      }
    },

    async cargarSolicitudDetalle({ commit }, solicitudId) {
      try {
        // Mostrar loader
        this.commit('utils/setLoader', true, { root: true });
        commit('setCargandoSolicitud', true);
        
        const response = await sancionesRecursosHumanosService.fetchSolicitudDetalle(solicitudId);
        commit('setlistaSanciones', response.listaSanciones || []);
        commit('setSolicitudActiva', response);
        return response;
      } catch (error) {
        commit('setSolicitudActiva', null);
        throw error;
      } finally {
        commit('setCargandoSolicitud', false);
        // Ocultar loader
        this.commit('utils/setLoader', false, { root: true });
      }
    },

    seleccionarSolicitud({ commit }, solicitud) {
      commit('setSolicitudActiva', solicitud);
    },

    limpiarSolicitudActiva({ commit }) {
      commit('setSolicitudActiva', null);
    },

    seleccionarSancionHistorica({ commit }, sancion) {
      commit('setSancionHistoricaActiva', sancion);
    },

    limpiarSancionHistoricaActiva({ commit }) {
      commit('setSancionHistoricaActiva', null);
    },
  },
};
